import {
  Checkbox,
  Radio,
  Form,
  Input,
  Row,
  Col,
  FormInstance,
  Select,
} from "antd";
import { useCallback, useMemo, useState } from "react";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { AchHelpModal } from "../AchHelpModal";
import { useGetEmailFormRules } from "../../../../../lib/hooks/getEmailFormRules";
import { PhoneInput } from "../../../../../components/PhoneInput";
import {
  FIRST_NAME_REQUIRED,
  LAST_NAME_REQUIRED,
  MAX_ADDRESS_LINE,
  MAX_CITY,
  MAX_FIRSTNAME,
  MAX_LASTNAME,
  WRONG_FIRST_NAME,
  WRONG_LAST_NAME,
} from "../../../../../lib/constants";
import { NAME_RULE } from "../../../../../lib/validationRules";
import { useGetUSAStates } from "../../../../../lib/hooks/getUSAStates";
import { useZipCodeRules } from "../../../../../lib/hooks/useZipCodeRules";

type FluidPayACHOptionProps = {
  form: FormInstance;
  title: string;
  providerOptions: any;
  selectedOption: string;
  setRequest: React.Dispatch<React.SetStateAction<any>>;
};

export const FluidPayACHOption = ({
  form,
  title,
  providerOptions,
  selectedOption,
  setRequest,
}: FluidPayACHOptionProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const open = useCallback(() => setIsOpen(true), []);

  const close = useCallback(() => setIsOpen(false), []);

  const { rules: emailRules } = useGetEmailFormRules(true);

  const { statesList } = useGetUSAStates(false);

  const { rules } = useZipCodeRules(true);

  const initContainerClassName: string = useMemo(
    () => `${selectedOption === "1" ? "" : "d-none"}`,
    [selectedOption],
  );

  const helpModalContainerClassName: string = useMemo(
    () => `${selectedOption === "1" ? "" : "d-none"} help-modal-container`,
    [selectedOption],
  );

  const billingInfoModalContainerClassName: string = useMemo(
    () =>
      `${selectedOption === "1" ? "d-block" : "d-none"} billing-info__container`,
    [selectedOption],
  );

  return (
    <Radio
      key="payment_form"
      value="1"
      className={`method mr-0 gateway fluid-ach ${selectedOption === "1" ? "" : "hoverable"}`}
    >
      <Row className="w-100 position-relative">
        <Row className="fluid-ach__container d-block">
          <span className="title">New {title}</span>
          <Row id="init-container" className={initContainerClassName} />
        </Row>
        <Row className={helpModalContainerClassName}>
          <p className="my-0 ant-btn-link" onClick={open}>
            <QuestionCircleOutlined className="mr-2" />
            How to find my banking data
          </p>
        </Row>
        <AchHelpModal isOpen={isOpen} close={close} />
      </Row>
      <Row
        className={`${initContainerClassName} mt-6 ${billingInfoModalContainerClassName}`}
      >
        <p className="title my-0">Billing</p>
        <Row className="mt-3" gutter={[8, 16]}>
          <Col span={24} md={12}>
            <Form.Item
              className="mb-0 w-100 "
              name="billingFirstName"
              label=""
              rules={[
                {
                  max: 50,
                  message: MAX_FIRSTNAME,
                },
                {
                  pattern: NAME_RULE,
                  message: WRONG_FIRST_NAME,
                },
                {
                  required: true,
                  message: FIRST_NAME_REQUIRED,
                },
              ]}
            >
              <Input placeholder="First Name" className="fluidpay-input" />
            </Form.Item>
          </Col>
          <Col span={24} md={12}>
            <Form.Item
              className="mb-0 w-100"
              name="billingLastName"
              label=""
              rules={[
                {
                  max: 50,
                  message: MAX_LASTNAME,
                },
                {
                  pattern: NAME_RULE,
                  message: WRONG_LAST_NAME,
                },
                {
                  required: true,
                  message: LAST_NAME_REQUIRED,
                },
              ]}
            >
              <Input placeholder="Last Name" className="fluidpay-input" />
            </Form.Item>
          </Col>
        </Row>
        <Row className="mt-4" gutter={[8, 16]}>
          <Col span={24} md={12}>
            <Form.Item
              label=""
              name="billingEmail"
              className="w-100 mb-0"
              rules={emailRules}
              normalize={(value: string) => value || null}
            >
              <Input placeholder="Email" className="fluidpay-input" />
            </Form.Item>
          </Col>
          <Col span={24} md={12}>
            <PhoneInput
              form={form}
              name="billingPhone"
              label=""
              size="middle"
              formItemMargin="mb-0"
              hideAddon
              placeholder="Phone"
              inputClassName="fluidpay-input"
            />
          </Col>
        </Row>
        {providerOptions["Enabled.BillingInfo.Address"] === true && (
          <>
            <Row className="mt-8" gutter={[8, 16]}>
              <Col span={24}>
                <Form.Item
                  label=""
                  name="billingAddress"
                  className="w-100 mb-0"
                  rules={[
                    {
                      max: 100,
                      message: MAX_ADDRESS_LINE,
                      validateTrigger: "onSubmit",
                    },
                  ]}
                >
                  <Input
                    placeholder="Billing Address"
                    className="fluidpay-input"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row className="mt-4" gutter={[8, 16]}>
              <Col span={8}>
                <Form.Item
                  className="mb-0 w-100 "
                  name="billingCity"
                  label=""
                  rules={[
                    {
                      max: 50,
                      message: MAX_CITY,
                      validateTrigger: "onSubmit",
                    },
                  ]}
                >
                  <Input placeholder="City" className="fluidpay-input" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item className="mb-0 w-100" name="billingState" label="">
                  <Select
                    options={statesList}
                    allowClear
                    showSearch
                    placeholder="State"
                    className="fluidpay-input"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  className="mb-0 w-100"
                  name="billingZipCode"
                  label=""
                  rules={rules}
                  normalize={(value: string) => value || null}
                >
                  <Input placeholder="Zip" className="fluidpay-input" />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
      </Row>
      <Row
        className={`${initContainerClassName} mt-5 save-for-future__container`}
      >
        <Form.Item name="saveForFuture" valuePropName="checked">
          <Checkbox>
            <span className="save-checkbox">Save for future payments</span>
          </Checkbox>
        </Form.Item>
      </Row>
    </Radio>
  );
};
